/**
 * Board Layout Configuration
 *
 * Centralized positioning system for LudoBoard component.
 * Modify these values to adjust the board layout.
 */

import type { PlayerInfo } from '../constants/LudoBoard.types';

export interface BoardComponentPosition {
  x: number;
  y: number;
  scale: number;
}

export interface BoardLayout {
  viewBox: {
    width: number;
    height: number;
  };
  bases: {
    yellow: BoardComponentPosition;
    blue: BoardComponentPosition;
    green: BoardComponentPosition;
    red: BoardComponentPosition;
  };
  roadSections: {
    top: BoardComponentPosition;
    right: BoardComponentPosition;
    bottom: BoardComponentPosition;
    left: BoardComponentPosition;
  };
}

/**
 * Core board configuration
 */
export const BOARD_CONFIG = {
  size: 450,
  viewBoxHeight: 800, // Extended height to accommodate dice and pseudo displays

  baseScale: 0.35, // 486×0.4=194.4px wide, 523×0.4=209.2px tall
  roadScale: 0.5, // 268×0.5=134px, 616×0.5=308px
  roadGroupScale: 1.0,

  basePositions: {
    yellow: { x: 20, y: 135 },
    blue: { x: 270, y: 135 },
    green: { x: 20, y: 400 },
    red: { x: 270, y: 400 }
  },

  roadPositions: {
    top: { x: 163, y: 90 },
    right: { x: 223.5, y: 113 },
    bottom: { x: 163, y: 309.5 },
    left: { x: 12, y: 113 }
  },

  // Player UI positioning (dice and `pseudo displays)
  playerUI: {
    // Scales for dice and pseudo boxes
    diceScale: 0.45,
    pseudoScale: 0.85,

    // Top section bases (Yellow, Blue) - Dice above base, Pseudo below dice
    topSection: {
      dice: { x: 130, y: -260 },
      pseudo: { x: 40, y: -240 }
    },

    // Bottom section bases (Green, Red) - Pseudo above dice, Dice below pseudo
    bottomSection: {
      pseudo: { x: 40, y: 320 },
      dice: { x: 120, y: 645 }
    }
  },

  // Road sections transform configuration
  roadCenterTransform: {
    translateTo: { x: 222, y: 309 }, // Move to this point
    pivotPoint: { x: 225, y: 225 }, // Scale around this pivot
    scale: 1.045
  }
} as const;

/**
 * Complete board layout configuration
 */
export const BOARD_LAYOUT = {
  viewBox: {
    width: BOARD_CONFIG.size,
    height: BOARD_CONFIG.size
  },
  bases: {
    yellow: { ...BOARD_CONFIG.basePositions.yellow, scale: BOARD_CONFIG.baseScale },
    blue: { ...BOARD_CONFIG.basePositions.blue, scale: BOARD_CONFIG.baseScale },
    green: { ...BOARD_CONFIG.basePositions.green, scale: BOARD_CONFIG.baseScale },
    red: { ...BOARD_CONFIG.basePositions.red, scale: BOARD_CONFIG.baseScale }
  },
  roadSections: {
    top: { ...BOARD_CONFIG.roadPositions.top, scale: 0.285 },
    right: { ...BOARD_CONFIG.roadPositions.right, scale: BOARD_CONFIG.roadScale },
    bottom: { ...BOARD_CONFIG.roadPositions.bottom, scale: 0.285 },
    left: { ...BOARD_CONFIG.roadPositions.left, scale: BOARD_CONFIG.roadScale }
  }
} as const satisfies BoardLayout;

/**
 * Constants for player display
 */

// Constant for showing all pawns in their base positions
export const ALL_PAWNS_VISIBLE = {
  pawn1: true,
  pawn2: true,
  pawn3: true,
  pawn4: true
} as const;

// Default values for player display
export const DEFAULT_PSEUDO = 'PSEUDO000';
export const DEFAULT_DICE_VALUE: 1 | 2 | 3 | 4 | 5 | 6 = 1;

/**
 * Utility Functions
 */

/**
 * Helper function to get player data with defaults
 */
export function getPlayerData(
  players: PlayerInfo[] | undefined,
  color: 'yellow' | 'blue' | 'green' | 'red'
): { pseudo: string; diceValue: 1 | 2 | 3 | 4 | 5 | 6 } {
  const player = players?.find((p) => p.color === color);
  return {
    pseudo: player?.pseudo ?? DEFAULT_PSEUDO,
    diceValue: player?.diceValue ?? DEFAULT_DICE_VALUE
  };
}

/**
 * Generate SVG transform string from position config
 */
export function getTransform(position: BoardComponentPosition): string {
  return `translate(${String(position.x)}, ${String(position.y)}) scale(${String(position.scale)})`;
}

/**
 * Generate SVG transform for center-based scaling
 */
export function getCenterScaleTransform(scale: number): string {
  const center = BOARD_CONFIG.size / 2;
  return `translate(${String(center)}, ${String(center)}) scale(${String(scale)}) translate(${String(-center)}, ${String(-center)})`;
}

/**
 * Generate SVG transform for player UI elements (dice or pseudo)
 */
export function getPlayerUITransform(position: { x: number; y: number }, scale: number): string {
  return `translate(${String(position.x)}, ${String(position.y)}) scale(${String(scale)})`;
}

/**
 * Generate SVG transform for road sections with custom center point
 * Original transform: translate(225, 310) scale(1.0) translate(-225, -225)
 */
export function getRoadCenterTransform(): string {
  const { translateTo, pivotPoint, scale } = BOARD_CONFIG.roadCenterTransform;
  return `translate(${String(translateTo.x)}, ${String(translateTo.y)}) scale(${String(scale)}) translate(${String(-pivotPoint.x)}, ${String(-pivotPoint.y)})`;
}
